import * as React from 'react';
import type { ReactElement } from 'react';
import styled from 'styled-components';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from '@web-stories-wp/react';
import { useAPI } from '@web-stories-wp/story-editor/src/app/api';

import OptionsList from '@src/components/optionsList/optionsList';
import LoadingPill from '../../common/loadingPill';
import MediaError from '../common/mediaError';

const FOLDER_ITEM_TYPES = new Set(['collection', 'folder', 'gallery']);

const isFolderType = (type?: string) => {
  const normalizedType = type?.toLowerCase();
  return normalizedType ? FOLDER_ITEM_TYPES.has(normalizedType) : false;
};

type Provider = {
  mediaProviderId?: string;
  providerConfigurationKey?: string;
  title?: string;
  label?: string;
};

type TagOption = {
  type?: string;
  value: string;
  label: string;
  [key: string]: unknown;
};

type TagGroup = {
  label: string;
  group: TagOption[];
};

type SetSearchTermPayload = {
  folder?: string;
  searchTerm?: string;
  tag?: string;
  player?: string;
};

type Breadcrumb = {
  text: string;
  onClick: () => void;
};

type UseFolderNavigationProps = {
  selectedProvider?: Provider | null;
  setSearchTerm: (payload: SetSearchTermPayload) => void;
  setTags: (tags: TagGroup) => void;
  requiresFolderSelection?: boolean;
  onBreadcrumbChange?: (breadcrumb: Breadcrumb | null) => void;
  resetMedia?: (payload: { provider: string }) => void;
  currentFolder?: string;
  searchTerm?: string;
};

type UseFolderNavigationReturn = {
  renderFolderList: () => ReactElement | null;
  shouldShowAssets: boolean;
  resetToRoot: () => void;
};

const FolderListContainer = styled.div`
  margin-bottom: 12px;
  flex-shrink: 0;
`;

const FolderListWrapper = styled.div`
  margin-bottom: 12px;
  flex-shrink: 0;
`;

const useFolderNavigation = ({
  selectedProvider,
  setSearchTerm,
  setTags,
  requiresFolderSelection = false,
  onBreadcrumbChange,
  resetMedia,
  currentFolder,
}: UseFolderNavigationProps): UseFolderNavigationReturn => {
  const localSearchTermRef = useRef<string>('');
  const [folders, setFolders] = useState<TagOption[]>([]);
  const [currentFolderPath, setCurrentFolderPath] = useState('');
  const [folderPathHistory, setFolderPathHistory] = useState<string[]>([]);
  const [folderLabelHistory, setFolderLabelHistory] = useState<string[]>([]);
  const [currentFolderLabel, setCurrentFolderLabel] = useState('');
  const [isFoldersLoading, setIsFoldersLoading] = useState(false);
  const [hasFolderLoadError, setHasFolderLoadError] = useState(false);
  const [folderRequestVersion, setFolderRequestVersion] = useState(0);

  const {
    actions: { getMediaProviderCollections },
  } = useAPI() as {
    actions: {
      getMediaProviderCollections: (args: {
        mediaProviderId: string;
        searchTerm: string;
        folder: string;
      }) => Promise<{ tags: TagOption[] }>;
    };
  };

  const isActive = Boolean(selectedProvider);
  const providerIdentity = selectedProvider
    ? `${selectedProvider.providerConfigurationKey ?? ''}:${
        selectedProvider.mediaProviderId ?? ''
      }`
    : '';
  const previousProviderIdentityRef = useRef(providerIdentity);
  const providerChanged =
    previousProviderIdentityRef.current !== providerIdentity;

  const resetToRoot = useCallback(() => {
    setFolderPathHistory([]);
    setFolderLabelHistory([]);
    setCurrentFolderPath('');
    setCurrentFolderLabel('');
  }, []);

  useEffect(() => {
    if (!providerChanged) {
      return;
    }

    previousProviderIdentityRef.current = providerIdentity;
    setFolders([]);
    setHasFolderLoadError(false);
    resetToRoot();
  }, [providerChanged, providerIdentity, resetToRoot]);

  useEffect(() => {
    const mediaProviderId = selectedProvider?.mediaProviderId;
    if (!isActive || !mediaProviderId) {
      return;
    }

    let isCurrentRequest = true;
    const requestedFolderPath = providerChanged ? '' : currentFolderPath;

    setIsFoldersLoading(true);
    setHasFolderLoadError(false);
    getMediaProviderCollections({
      mediaProviderId,
      searchTerm: '',
      folder: requestedFolderPath,
    })
      .then(({ tags: newTags }) => {
        if (!isCurrentRequest || localSearchTermRef.current !== '') {
          return;
        }

        const folderItems = newTags.filter((item) => isFolderType(item.type));
        const tagItems = newTags.filter((item) => !isFolderType(item.type));

        setFolders(folderItems);
        setTags({
          label: 'Most Used Tags',
          group: tagItems,
        });

        if (!requestedFolderPath) {
          setSearchTerm({ searchTerm: '', folder: '' });
        }
      })
      .catch(() => {
        if (!isCurrentRequest) {
          return;
        }

        setFolders([]);
        setHasFolderLoadError(true);
      })
      .finally(() => {
        if (isCurrentRequest) {
          setIsFoldersLoading(false);
        }
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [
    currentFolderPath,
    folderRequestVersion,
    getMediaProviderCollections,
    isActive,
    providerChanged,
    selectedProvider?.mediaProviderId,
    setSearchTerm,
    setTags,
  ]);

  const selectFolder = useCallback(
    (folderId: string) => {
      const selectedFolder = folders.find(
        (folder) => folder.value === folderId
      );
      if (!selectedFolder) {
        return;
      }

      if (resetMedia && selectedProvider?.providerConfigurationKey) {
        resetMedia({ provider: selectedProvider.providerConfigurationKey });
      }

      setFolderPathHistory((history) => [...history, currentFolderPath]);
      setFolderLabelHistory((history) => [...history, currentFolderLabel]);
      setCurrentFolderPath(selectedFolder.value);
      setCurrentFolderLabel(selectedFolder.label ?? '');
      setSearchTerm({ searchTerm: '', folder: selectedFolder.value });
      setFolders([]);
      setIsFoldersLoading(true);
    },
    [
      currentFolderLabel,
      currentFolderPath,
      folders,
      resetMedia,
      selectedProvider,
      setSearchTerm,
    ]
  );

  const deselectFolder = useCallback(() => {
    if (resetMedia && selectedProvider?.providerConfigurationKey) {
      resetMedia({ provider: selectedProvider.providerConfigurationKey });
    }

    if (folderPathHistory.length > 0) {
      const previousPath = folderPathHistory[folderPathHistory.length - 1];
      const previousLabel =
        folderLabelHistory[folderLabelHistory.length - 1] ?? '';
      setFolderPathHistory((history) => history.slice(0, -1));
      setFolderLabelHistory((history) => history.slice(0, -1));
      setCurrentFolderPath(previousPath);
      setCurrentFolderLabel(previousLabel);
      setSearchTerm({ searchTerm: '', folder: previousPath });
      return;
    }

    setCurrentFolderPath('');
    setCurrentFolderLabel('');
    setSearchTerm({ searchTerm: '', folder: '' });
  }, [
    folderLabelHistory,
    folderPathHistory,
    resetMedia,
    selectedProvider,
    setSearchTerm,
  ]);

  const folderOptions = useMemo(() => {
    return folders.map((folder) => ({
      label: folder.label,
      key: folder.value,
    }));
  }, [folders]);

  const hasFolders = folderOptions.length > 0;
  const shouldShowFoldersList =
    hasFolders ||
    isFoldersLoading ||
    hasFolderLoadError ||
    (requiresFolderSelection && !currentFolderPath);
  const shouldShowAssets =
    !isFoldersLoading &&
    (!requiresFolderSelection || Boolean(currentFolderPath));

  useEffect(() => {
    if (!onBreadcrumbChange) {
      return;
    }

    const providerName =
      selectedProvider?.title ||
      selectedProvider?.label ||
      selectedProvider?.providerConfigurationKey ||
      'Provider';

    if (currentFolderPath) {
      const labelTrail = [...folderLabelHistory, currentFolderLabel].filter(
        Boolean
      );
      const displaySegments =
        labelTrail.length >= 2
          ? labelTrail.slice(-2)
          : [providerName, labelTrail[0]].filter(Boolean);
      const [previous, current] =
        displaySegments.length > 1
          ? displaySegments
          : [null, displaySegments[0]];

      const text = previous ? `${previous} | ${current}` : `${current}`;

      onBreadcrumbChange({
        text,
        onClick: deselectFolder,
      });
      return;
    }

    onBreadcrumbChange(null);
  }, [
    currentFolderLabel,
    currentFolderPath,
    deselectFolder,
    folderLabelHistory,
    onBreadcrumbChange,
    selectedProvider,
  ]);

  const previousFolderRef = useRef<string | undefined>(currentFolder);
  useEffect(() => {
    if (!isActive) {
      previousFolderRef.current = currentFolder;
      return;
    }

    const previousFolder = previousFolderRef.current;
    previousFolderRef.current = currentFolder;

    if (previousFolder && currentFolder === '') {
      resetToRoot();
    }
  }, [currentFolder, isActive, resetToRoot]);

  const renderFolderList = useCallback((): ReactElement | null => {
    if (!shouldShowFoldersList) {
      return null;
    }

    const Container =
      folderOptions.length > 3 ? FolderListContainer : FolderListWrapper;

    return (
      <Container>
        {isFoldersLoading ? (
          <LoadingPill />
        ) : hasFolderLoadError ? (
          <MediaError
            onRetry={() => setFolderRequestVersion((version) => version + 1)}
          />
        ) : (
          <OptionsList
            options={folderOptions}
            shouldDisplayOptionsList={folderOptions.length > 0}
            setChosenOption={selectFolder}
            disableAllOptions
            shouldDisplayNoOptionFoundMessage={folderOptions.length === 0}
          />
        )}
      </Container>
    );
  }, [
    folderOptions,
    hasFolderLoadError,
    isFoldersLoading,
    selectFolder,
    shouldShowFoldersList,
  ]);

  return {
    renderFolderList,
    shouldShowAssets,
    resetToRoot,
  };
};

export default useFolderNavigation;
